import { CommentClient } from "@formant/core/client/CommentClient";
import { defined } from "@formant/core/common/defined";
import { IEventStore } from "@formant/core/model/data/IEventStore";
import { IComment } from "@formant/core/model/IComment";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class CommentStore {
    private cache = new StoreCache<Uuid, IComment | false>();

    constructor(
        private commentClient: CommentClient,
        private authStore: IAuthStore,
        private eventStore: IEventStore,
    ) {}

    public get(id: Uuid): IComment | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async create(comment: IComment): Promise<IComment> {
        const created = await this.commentClient.create(
            await this.authStore.token(),
            comment,
        );
        this.cache.set(defined(created.id), created);
        this.eventStore.clear();
        return created;
    }

    public async update(id: Uuid, comment: Partial<IComment>) {
        const updated = await this.commentClient.update(
            await this.authStore.token(),
            id,
            comment,
        );
        this.cache.set(id, updated);
        this.eventStore.clear();
        return updated;
    }

    public async save(comment: IComment): Promise<IComment> {
        const { id } = comment;
        if (id) {
            return await this.update(id, {
                ...comment,
                editedAt: new Date().toISOString(),
            });
        } else {
            return await this.create(comment);
        }
    }

    public async delete(id: Uuid) {
        await this.commentClient.delete(await this.authStore.token(), id);
        this.cache.delete(id);
        this.eventStore.clear();
    }

    private async fetchGet(id: Uuid): Promise<IComment | false> {
        const comment = await this.commentClient.get(
            await this.authStore.token(),
            id,
        );
        const value = comment || false;
        this.cache.set(id, value);
        return value;
    }
}
